import api from "@/api/v1-jwt/config/api";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import {
  RecurringInvoiceSchedule,
  RecurringInvoiceScheduleSchema,
} from "../types";
import { createErrorHandler } from "@/utils/errorHandler";

type RecurringInvoiceActionParams = {
  userId: string;
  recurringInvoiceId: string;
};

const pauseRecurringInvoice = async ({
  userId,
  recurringInvoiceId,
}: RecurringInvoiceActionParams): Promise<RecurringInvoiceSchedule> => {
  const url = `/platform/invoices/${userId}/recurring/${recurringInvoiceId}/pause`;
  const response = await api.patch(url);
  return RecurringInvoiceScheduleSchema.parse(response.data.data);
};

const resumeRecurringInvoice = async ({
  userId,
  recurringInvoiceId,
}: RecurringInvoiceActionParams): Promise<RecurringInvoiceSchedule> => {
  const url = `/platform/invoices/${userId}/recurring/${recurringInvoiceId}/resume`;
  const response = await api.patch(url);
  return RecurringInvoiceScheduleSchema.parse(response.data.data);
};

const cancelRecurringInvoice = async ({
  userId,
  recurringInvoiceId,
}: RecurringInvoiceActionParams): Promise<RecurringInvoiceSchedule> => {
  const url = `/platform/invoices/${userId}/recurring/${recurringInvoiceId}`;
  const response = await api.delete(url);
  return RecurringInvoiceScheduleSchema.parse(response.data.data);
};

const skipNextRecurringInvoice = async ({
  userId,
  recurringInvoiceId,
}: RecurringInvoiceActionParams): Promise<RecurringInvoiceSchedule> => {
  const url = `/platform/invoices/${userId}/recurring/${recurringInvoiceId}/skip-next`;
  const response = await api.post(url);
  return RecurringInvoiceScheduleSchema.parse(response.data.data);
};

const advanceRecurringInvoice = async ({
  userId,
  recurringInvoiceId,
}: RecurringInvoiceActionParams): Promise<RecurringInvoiceSchedule> => {
  const url = `/platform/invoices/${userId}/recurring/${recurringInvoiceId}/advance`;
  const response = await api.post(url);
  return RecurringInvoiceScheduleSchema.parse(response.data.data);
};

const defaultErrorHandler = createErrorHandler({
  customHandlers: {
    400: () => "Invalid request",
    403: () => "Unauthorized",
    404: () => "Recurring invoice series not found",
  },
});

export const recurringInvoiceActions = {
  pause: {
    useMutation: (
      options?: UseMutationOptions<
        RecurringInvoiceSchedule,
        Error,
        RecurringInvoiceActionParams
      >
    ) =>
      useMutation<RecurringInvoiceSchedule, Error, RecurringInvoiceActionParams>(
        {
          mutationFn: pauseRecurringInvoice,
          onError: defaultErrorHandler,
          ...options,
        }
      ),
  },
  resume: {
    useMutation: (
      options?: UseMutationOptions<
        RecurringInvoiceSchedule,
        Error,
        RecurringInvoiceActionParams
      >
    ) =>
      useMutation<RecurringInvoiceSchedule, Error, RecurringInvoiceActionParams>(
        {
          mutationFn: resumeRecurringInvoice,
          onError: defaultErrorHandler,
          ...options,
        }
      ),
  },
  cancel: {
    useMutation: (
      options?: UseMutationOptions<
        RecurringInvoiceSchedule,
        Error,
        RecurringInvoiceActionParams
      >
    ) =>
      useMutation<RecurringInvoiceSchedule, Error, RecurringInvoiceActionParams>(
        {
          mutationFn: cancelRecurringInvoice,
          onError: defaultErrorHandler,
          ...options,
        }
      ),
  },
  skipNext: {
    useMutation: (
      options?: UseMutationOptions<
        RecurringInvoiceSchedule,
        Error,
        RecurringInvoiceActionParams
      >
    ) =>
      useMutation<RecurringInvoiceSchedule, Error, RecurringInvoiceActionParams>(
        {
          mutationFn: skipNextRecurringInvoice,
          onError: defaultErrorHandler,
          ...options,
        }
      ),
  },
  advance: {
    mutationFn: advanceRecurringInvoice,
    useMutation: (
      options?: UseMutationOptions<
        RecurringInvoiceSchedule,
        Error,
        RecurringInvoiceActionParams
      >
    ) =>
      useMutation<RecurringInvoiceSchedule, Error, RecurringInvoiceActionParams>(
        {
          mutationFn: advanceRecurringInvoice,
          onError: defaultErrorHandler,
          ...options,
        }
      ),
  },
};
